/**
 * =============================================================================
 * Hook: useActivityPlaybooks
 * Versão: 1.0.0
 *
 * Descrição:
 *   Lista todos os Playbooks de Atividades de um workspace, com contadores
 *   de steps, bindings e cadências ativas. Inclui mutations para
 *   upsert/delete via RPCs `upsert_activity_playbook` e
 *   `delete_activity_playbook`.
 *
 *   Padrão server-side abstraction: TODA leitura/escrita passa por RPC
 *   `SECURITY DEFINER` — nada de SELECT direto na tabela.
 *
 * Release notes:
 *   v1.0.0 - Criação (Fase 1, item 1.11 do Playbook de Vendas).
 * =============================================================================
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useActivityPlaybooks');

// ----------------------------------------------------------------------------
// Tipos
// ----------------------------------------------------------------------------
// Tipo de escopo do playbook
//  - 'flat'      => comportamento legado (todos os steps numa única lista)
//  - 'per_phase' => steps são agrupados por fase de um pipeline (cada step tem phase_id)
export type PlaybookScope = 'flat' | 'per_phase';

export interface ActivityPlaybookListItem {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  color: string | null;
  icon: string | null;
  is_active: boolean;
  is_default: boolean;
  scope?: PlaybookScope;
  pipeline_id?: string | null;
  steps_count: number;
  bindings_count: number;
  active_cadences_count: number;
  created_at: string;
  updated_at: string;
}

// Item da checklist de critérios de sucesso (Playbook v2.0 - Roteiro)
export interface SuccessCriterion {
  id: string;
  label: string;
  required: boolean;
}

// FAQ de objeções do step (Playbook v2.0 - Roteiro)
export interface StepObjection {
  id: string;
  question: string;
  answer_html: string;
}

export interface UpsertPlaybookStepInput {
  /** UUID real do step (presente apenas quando editando step já existente).
   * Backend faz UPDATE preservando playbook_step_id nas cadências ativas
   * (Live Edit Sempre — playbook v2.1.0). */
  id?: string;
  sort_order?: number;
  activity_type: string;
  title: string;
  description_html?: string | null;
  channel?: string | null;
  due_offset_value?: number;
  due_offset_unit?: 'minutes' | 'hours' | 'days' | 'business_days';
  due_anchor?: 'cadence_start' | 'previous_step_completed' | 'deal_created' | 'phase_entered';
  is_required?: boolean;
  sla_hours?: number | null;
  auto_complete_on?: string | null;
  // Em modo per_phase, identifica a fase à qual o step pertence (obrigatório no backend).
  phase_id?: string | null;
  // Roteiro (Playbook v2.0)
  script_html?: string | null;
  goal?: string | null;
  success_criteria?: SuccessCriterion[];
  objections?: StepObjection[];
  script_variables_used?: string[];
}

export interface UpsertPlaybookInput {
  id?: string | null;
  workspace_id: string;
  name: string;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  is_active?: boolean;
  is_default?: boolean;
  // Modo de organização dos steps (default 'flat' para compatibilidade)
  scope?: PlaybookScope;
  // Obrigatório quando scope = 'per_phase'
  pipeline_id?: string | null;
  // [v2.1.0] Permissões do vendedor
  allow_seller_cancel_step?: boolean;
  allow_seller_create_manual_step?: boolean;
  steps?: UpsertPlaybookStepInput[];
}

// ----------------------------------------------------------------------------
// Hook principal — lista + mutations
// ----------------------------------------------------------------------------
export function useActivityPlaybooks(workspaceId: string | null | undefined) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id ?? null;

  // Lista de playbooks — quando workspaceId é null/undefined, retorna
  // playbooks de TODOS os workspaces do tenant em uma única chamada
  // (suportado pela RPC v1.0.2, que torna p_workspace_id opcional).
  const listQuery = useQuery({
    queryKey: ['activity-playbooks', tenantId, workspaceId ?? 'all'],
    enabled: !!tenantId,
    queryFn: async (): Promise<ActivityPlaybookListItem[]> => {
      const { data, error } = await supabase.rpc('list_activity_playbooks', {
        p_workspace_id: workspaceId ?? null,
        p_tenant_hint: tenantId,
      });
      if (error) {
        logger.error('Erro ao listar playbooks', error);
        throw error;
      }
      return (data ?? []) as ActivityPlaybookListItem[];
    },
  });

  // Upsert (criar/editar)
  const upsertMutation = useMutation({
    mutationFn: async (payload: UpsertPlaybookInput) => {
      const { data, error } = await supabase.rpc('upsert_activity_playbook', {
        p_payload: payload as any,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return data as string; // UUID do playbook
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity-playbooks', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['activity-playbook-details'] });
    },
  });

  // Delete
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc('delete_activity_playbook', {
        p_id: id,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activity-playbooks', tenantId] });
    },
  });

  return {
    playbooks: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    isFetching: listQuery.isFetching,
    error: listQuery.error,
    refetch: listQuery.refetch,
    upsertPlaybook: upsertMutation.mutateAsync,
    isUpserting: upsertMutation.isPending,
    deletePlaybook: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
}
